import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ALL_GUEST_PERMISSIONS,
  GUEST_PERMISSION_LABELS,
  type GuestLink,
  type GuestPermission,
} from '@avslack/shared';
import { createGuestLink, listGuestLinks, revokeGuestLink } from '../lib/api';
import { useUsers } from '../data/users';
import { Dialog } from './Dialog';
import { Icon } from './Icon';

const DURATIONS: { hours: 2 | 24 | 168; label: string }[] = [
  { hours: 2, label: '2 óra' },
  { hours: 24, label: '24 óra' },
  { hours: 168, label: '7 nap' },
];

/**
 * VENDEGLINK a csatorna hivasahoz (H2).
 *
 * Kulso fel (pl. egy klinika munkatarsa) fiok nelkul, bongeszobol csatlakozik:
 * nevet ad meg, varakozik, es a hivasban barki beengedi. Itt allithato, mit
 * tehet (alapbol mindent), es meddig el a link. A link csak most latszik
 * egyben - kesobb nem allithato elo, csak visszavonhato.
 */
export function GuestLinkDialog({
  channelId,
  open,
  onOpenChange,
}: {
  channelId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { nameOf } = useUsers();
  const [permissions, setPermissions] = useState<GuestPermission[]>(ALL_GUEST_PERMISSIONS);
  const [hours, setHours] = useState<2 | 24 | 168>(2);
  const [created, setCreated] = useState<GuestLink | null>(null);
  const [copied, setCopied] = useState(false);

  const linksKey = ['guest-links', channelId] as const;
  const links = useQuery({
    queryKey: linksKey,
    queryFn: () => listGuestLinks(channelId).then((result) => result.links),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () => createGuestLink(channelId, { permissions, expiresInHours: hours }),
    onSuccess: ({ link }) => {
      setCreated(link);
      setCopied(false);
      void queryClient.invalidateQueries({ queryKey: linksKey });
    },
  });

  const revoke = useMutation({
    mutationFn: (linkId: string) => revokeGuestLink(linkId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: linksKey }),
  });

  const toggle = (permission: GuestPermission) =>
    setPermissions((current) =>
      current.includes(permission)
        ? current.filter((value) => value !== permission)
        : [...current, permission],
    );

  const copy = async (url: string) => {
    await navigator.clipboard.writeText(url).catch(() => undefined);
    setCopied(true);
  };

  const share = (url: string) =>
    void navigator
      .share({ title: 'Meghívó AVSlack-hívásba', text: 'Csatlakozz a hívásomhoz:', url })
      .catch(() => undefined);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setCreated(null);
        onOpenChange(next);
      }}
      title="Vendég meghívása"
      description="Külső fél fiók nélkül, böngészőből csatlakozhat. Nevet ad meg, és a hívásban bárki beengedheti."
    >
      {created?.url ? (
        <section className="space-y-3 text-sm">
          <p className="text-ink-muted">
            Küldd el ezt a linket. Csak most látszik egyben - később csak visszavonni lehet.
          </p>
          <div className="flex items-center gap-2 rounded-control border border-border bg-surface px-3 py-2">
            <code className="min-w-0 flex-1 truncate text-xs">{created.url}</code>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void copy(created.url!)}
              className="rounded-control bg-accent px-3 py-2 text-xs font-semibold text-accent-ink hover:bg-accent-hover"
            >
              <Icon name="masolas" size="sm" /> {copied ? 'Másolva' : 'Link másolása'}
            </button>
            {typeof navigator.share === 'function' && (
              <button
                type="button"
                onClick={() => share(created.url!)}
                className="rounded-control border border-border px-3 py-2 text-xs font-semibold hover:bg-surface-hover"
              >
                <Icon name="megosztas" size="sm" /> Megosztás…
              </button>
            )}
            <button
              type="button"
              onClick={() => setCreated(null)}
              className="rounded-control px-3 py-2 text-xs text-ink-muted hover:bg-surface-hover"
            >
              Új link
            </button>
          </div>
        </section>
      ) : (
        <section className="space-y-4 text-sm">
          <fieldset>
            <legend className="mb-1.5 font-semibold">Mit tehet a vendég?</legend>
            <div className="flex flex-wrap gap-2">
              {ALL_GUEST_PERMISSIONS.map((permission) => (
                <label
                  key={permission}
                  className="flex cursor-pointer items-center gap-1.5 rounded-pill border border-border px-3 py-1 text-xs"
                >
                  <input
                    type="checkbox"
                    checked={permissions.includes(permission)}
                    onChange={() => toggle(permission)}
                    className="accent-[var(--accent)]"
                  />
                  {GUEST_PERMISSION_LABELS[permission]}
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-ink-faint">
              Hívás közben bármikor bővíthető vagy szűkíthető.
            </p>
          </fieldset>

          <fieldset>
            <legend className="mb-1.5 font-semibold">Meddig érvényes?</legend>
            <div className="flex gap-2">
              {DURATIONS.map((option) => (
                <label
                  key={option.hours}
                  className="flex cursor-pointer items-center gap-1.5 rounded-pill border border-border px-3 py-1 text-xs"
                >
                  <input
                    type="radio"
                    name="guest-link-duration"
                    checked={hours === option.hours}
                    onChange={() => setHours(option.hours)}
                    className="accent-[var(--accent)]"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </fieldset>

          {create.isError && (
            <p role="alert" className="text-xs text-danger">
              {create.error instanceof Error ? create.error.message : 'A link nem készült el.'}
            </p>
          )}
          <button
            type="button"
            disabled={create.isPending}
            onClick={() => create.mutate()}
            className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-ink hover:bg-accent-hover disabled:opacity-50"
          >
            {create.isPending ? 'Készül…' : 'Link készítése'}
          </button>
        </section>
      )}

      {(links.data?.length ?? 0) > 0 && (
        <section className="mt-5 border-t border-border-soft pt-3 text-xs">
          <h3 className="mb-1.5 font-semibold">Érvényes linkek</h3>
          <ul className="space-y-1.5">
            {links.data!.map((link) => (
              <li key={link.id} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-ink-muted">
                  {link.createdBy ? nameOf(link.createdBy) : '—'} ·{' '}
                  {link.permissions.map((p) => GUEST_PERMISSION_LABELS[p]).join(', ') || 'csak néz'}{' '}
                  · lejár{' '}
                  {new Date(link.expiresAt).toLocaleString('hu-HU', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}
                </span>
                <button
                  type="button"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(link.id)}
                  className="shrink-0 rounded px-2 py-1 font-medium text-danger hover:bg-danger-soft"
                >
                  Visszavonás
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Dialog>
  );
}
